<template>
  <div class="pos-map">
    <transition name="fade" mode="out-in">
      <loader-with-message v-if="dataLoading"
                           :key="'loaderpos'"
                           :has-error="dataFetchError"
                           @retry="getPointsOfServices"/>
      <div v-else :key="'map-content'">
        <base-heading>{{ $t('support.pointOfService.title') }}</base-heading>
        <google-map ref="googleMap">
          <template slot-scope="{ google, map }">
            <map-marker v-for="(marker, index) in markers"
                        :key="'marker_'+index"
                        :ref="'marker'+index"
                        :marker="marker"
                        :google="google"
                        :map="map"/>
          </template>
        </google-map>
        <label class="label">{{ $t('support.pointOfService.selectCityLabel') }}</label>
        <div class="drop-wrapper">
          <v-select class="select-dropdown"
                    required
                    v-model="selectedCity"
                    :options="citiesList"
                    label="name"
                    :placeholder="$t('support.pointOfService.placeholderText')"
                    @input="filterPointsOfService">
            <span slot="no-options">{{ $t('common.noData') }}</span>
          </v-select>
        </div>
        <base-table :data="posData" :columns="tableCol" class="rounded-table pos-table">
          <div class="no-content" slot="no-content">{{ $t('support.pointOfService.posTable.noPosLabel') }}</div>
        </base-table>
      </div>
    </transition>
  </div>
</template>

<script>
import GoogleMap from '@/components/common/googleMap/googleMap'
import MapMarker from '@/components/common/googleMap/mapMarker'
import { getRef } from '@/utils/getRefElement'
import { posCols } from '@/components/support/posConfig'
import BaseTable from '@/components/base/table/base-table'
import LoaderWithMessage from '@/components/common/loader-with-message'
import { mapConfig } from '@/components/common/googleMap/googleMapConfig'

export default {
  name: 'pos-map',
  components: { MapMarker, GoogleMap, BaseTable, LoaderWithMessage },
  data () {
    return {
      mapKey: new Date().getTime(),
      markers: [],
      tableCol: posCols,
      selectedCity: '',
      citiesList: ['Svi gradovi'],
      dataLoading: true,
      dataFetchError: false
    }
  },
  computed: {
    allCities () {
      return this.selectedCity === 'Svi gradovi'
    },
    posData () {
      const arr = []
      this.markers.map(item => {
        const summerWeekdayTime = item?.openingHours?.weekDayOpeningList?.find(item => item.weekDay === 'SUMMER_WEEKDAYS')
        const summerSaturdayTime = item?.openingHours?.weekDayOpeningList?.find(item => item.weekDay === 'SUMMER_SATURDAY')
        const summerSundayTime = item?.openingHours?.weekDayOpeningList?.find(item => item.weekDay === 'SUMMER_SUNDAY')
        const winterWeekdayTime = item?.openingHours?.weekDayOpeningList?.find(item => item.weekDay === 'WINTER_WEEKDAYS')
        const winterSaturdayTime = item?.openingHours?.weekDayOpeningList?.find(item => item.weekDay === 'WINTER_SATURDAY')
        const winterSundayTime = item?.openingHours?.weekDayOpeningList?.find(item => item.weekDay === 'WINTER_SUNDAY')

        arr.push({
          name: item.name,
          address: item?.address?.formattedAddress,
          summerWorkingTime: `pon-pet: ${summerWeekdayTime?.openingTime?.formattedHour}-${summerWeekdayTime?.closingTime?.formattedHour}h <br/>
          sub: ${summerSaturdayTime ? summerSaturdayTime?.openingTime?.formattedHour + '-' + summerSaturdayTime?.closingTime?.formattedHour + 'h' : 'ne radi'} <br/>
          ned: ${summerSundayTime ? summerSundayTime?.openingTime?.formattedHour + '-' + summerSundayTime?.closingTime?.formattedHour + 'h' : 'ne radi'}
          `,
          winterWorkingTime: `pon-pet: ${winterWeekdayTime?.openingTime?.formattedHour}-${winterWeekdayTime?.closingTime?.formattedHour}h <br/>
          sub: ${winterSaturdayTime ? winterSaturdayTime?.openingTime?.formattedHour + '-' + winterSaturdayTime?.closingTime?.formattedHour + 'h' : 'ne radi'} <br/>
          ned: ${winterSundayTime ? winterSundayTime?.openingTime?.formattedHour + '-' + winterSundayTime?.closingTime?.formattedHour + 'h' : 'ne radi'}
          `
        })
      })
      return arr
    }
  },
  methods: {
    getPointsOfServices (onInit = false) {
      const endPoint = onInit || this.allCities ? 'stores/pos' : `stores/pos?cityName=${this.selectedCity}`
      this.dataFetchError = false
      this.$apiRequest.get(endPoint)
        .then(response => {
          if (response.status === 200) {
            this.dataFetchError = false
            this.markers = response.data.pointOfServices.filter(item => item.type === 'STORE') || []
            this.markers.map(item => {
              if (this.citiesList.indexOf(item.address.town) === -1) {
                this.citiesList.push(item.address.town)
              }
            })

            this.dataLoading = false

            if (!onInit) {
              this.zoomSelectedMarkers(this.allCities)
              setTimeout(() => {
                this.initMarkersByRef()
              }, 300)
            }
          }
        })
        .catch(e => {
          this.dataFetchError = true
        })
    },
    filterPointsOfService () {
      this.clearAllMarkers()
      this.getPointsOfServices()
    },
    clearAllMarkers () {
      const existingMarkers = this.$refs.googleMap.google.maps._addedMarkers
      if (existingMarkers && existingMarkers.length) {
        for (let i = 0; i < existingMarkers.length; i++) {
          existingMarkers[i].setMap(null)
        }
        this.$refs.googleMap.google.maps._addedMarkers.length = 0
      }
    },
    initMarkersByRef () {
      if (Object.keys(this.$refs).length) {
        Object.keys(this.$refs).forEach(ref => {
          if (ref.indexOf('marker') > -1 && this.$refs[ref].length) {
            const refElement = getRef(this.$refs[ref])
            refElement.initMapMarkers()
          }
        })
      }
    },
    zoomSelectedMarkers (allCities = true) {
      let avgLat = 0
      let avgLon = 0
      this.markers.forEach(marker => {
        avgLat += marker.geoPoint.latitude
        avgLon += marker.geoPoint.longitude
      })
      const cityConfig = {
        center: {
          lat: avgLat / this.markers.length,
          lng: avgLon / this.markers.length
        },
        zoom: 14
      }

      const selectedConfig = allCities ? mapConfig : cityConfig

      this.$refs.googleMap.zoomMap(selectedConfig)
    }
  },
  mounted () {
    this.getPointsOfServices(true)
  }
}
</script>

<style scoped lang="scss">
.pos-map {
  padding: 4rem 2rem;

  @media (map_get($breakpoints, sm)) {
    padding-right: 0;
  }

  .pos-table /deep/ .custom-padding {
    padding-left: 2rem;
    padding-right: 2rem;
  }

  .label {
    display: block;
    margin: 2rem 0 0.8rem;
    color: $dark-grey;
    font-size: 1.2rem;
  }

  .drop-wrapper {
    margin-bottom: 2rem;
  }
}
</style>
